import { Map as OlMap } from "ol";
import TileLayer from "ol/layer/Tile";
import { getRenderPixel } from "ol/render";
import RenderEvent from "ol/render/Event";
import TileSource from "ol/source/Tile";
import { useCallback, useEffect } from "react";

type Props = {
  swiperValue: number;
  olMap: OlMap;
  afterTileLayer: TileLayer<TileSource>;
};

export const useLayerSwiper = ({
  swiperValue,
  olMap: map,
  afterTileLayer,
}: Props) => {
  useEffect(() => {
    if (!afterTileLayer) return;
    afterTileLayer.on("prerender", onPrerender);
    afterTileLayer.on("postrender", onPostRender);
    return () => {
      afterTileLayer?.hasListener("prerender") &&
        afterTileLayer.un("prerender", onPrerender);
      afterTileLayer?.hasListener("postrender") &&
        afterTileLayer.un("postrender", onPostRender);
    };
  }, [afterTileLayer, swiperValue]);

  // 렌더링 하기전에 영역을 계산해서 클리핑한다.
  const onPrerender = useCallback(
    (event: RenderEvent) => {
      if (!event) return;

      const ctx = event.context;
      if (!(ctx instanceof CanvasRenderingContext2D)) return;
      const mapSize = map?.getSize();
      if (typeof mapSize === "undefined") return;
      const mapWidth = mapSize[0];
      const mapHeight = mapSize[1];

      const clippingX = mapWidth * (Number(swiperValue) / 100);

      const tl = getRenderPixel(event, [clippingX, 0]);
      const tr = getRenderPixel(event, [mapWidth, 0]);
      const bl = getRenderPixel(event, [clippingX, mapHeight]);
      const br = getRenderPixel(event, [mapWidth, mapHeight]);

      ctx.save();
      ctx.beginPath();
      ctx.moveTo(tl[0], tl[1]);
      ctx.lineTo(bl[0], bl[1]);
      ctx.lineTo(br[0], br[1]);
      ctx.lineTo(tr[0], tr[1]);
      ctx.closePath();
      ctx.clip();
    },
    [map, swiperValue]
  );

  // 레이어를 렌더링 후 값을 변경한다.
  const onPostRender = useCallback(
    (event: RenderEvent) => {
      if (!event) return;
      const ctx = event.context;
      if (!(ctx instanceof CanvasRenderingContext2D)) return;
      ctx.restore();
    },
    [map, swiperValue]
  );

  const rerender = () => {
    if (!map) return;
    map.render();
  };

  return {
    rerender,
  };
};
